import {
  CheckCircle2,
  Fingerprint,
  ScanFace,
  Shield,
  Smartphone,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@docuid/ui/lib/utils';

type Phase = 'idle' | 'scanning' | 'verifying' | 'success';

const SCAN_INTERVAL_MS = 50;
const SCANNING_DURATION_MS = 3000;
const VERIFYING_DURATION_MS = 1500;
const SUCCESS_DURATION_MS = 2500;
const RESTART_DELAY_MS = 1000;
const INITIAL_DELAY_MS = 1200;

const STAGES: readonly { id: string; label: string; Icon: LucideIcon }[] = [
  { id: 'device', label: 'Your device', Icon: Smartphone },
  { id: 'network', label: 'iVALT network', Icon: Shield },
  { id: 'verified', label: 'Verified', Icon: CheckCircle2 },
];

const PHASE_STAGE: Record<Phase, number> = {
  idle: 0,
  scanning: 0,
  verifying: 1,
  success: 2,
};

export function IvaltAuthDemo() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [scanLine, setScanLine] = useState(0);
  const timersReference = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    for (const timer of timersReference.current) {
      clearTimeout(timer);
    }
    timersReference.current = [];
  }, []);

  const start = useCallback(() => {
    clearTimers();
    setPhase('scanning');
    setScanLine(0);

    const scanInterval = setInterval(() => {
      setScanLine((previous) => (previous >= 100 ? 0 : previous + 5));
    }, SCAN_INTERVAL_MS);

    const scanningTimer = setTimeout(() => {
      setPhase('verifying');
      clearInterval(scanInterval);
    }, SCANNING_DURATION_MS);

    const verifyingTimer = setTimeout(() => {
      setPhase('success');
    }, SCANNING_DURATION_MS + VERIFYING_DURATION_MS);

    const loopTimer = setTimeout(
      () => {
        setPhase('idle');
        setScanLine(0);
        const restartTimer = setTimeout(() => {
          start();
        }, RESTART_DELAY_MS);
        timersReference.current.push(restartTimer);
      },
      SCANNING_DURATION_MS + VERIFYING_DURATION_MS + SUCCESS_DURATION_MS,
    );

    timersReference.current.push(scanningTimer, verifyingTimer, loopTimer);
  }, [clearTimers]);

  useEffect(() => {
    const initialTimer = setTimeout(() => {
      start();
    }, INITIAL_DELAY_MS);
    timersReference.current.push(initialTimer);
    return clearTimers;
  }, [start, clearTimers]);

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="border-border bg-card relative flex h-105 w-55 flex-col overflow-hidden rounded-[2.25rem] border-[6px]">
        <div className="bg-muted absolute top-1.5 left-1/2 h-1.5 w-14 -translate-x-1/2 rounded-full" />

        <div className="flex flex-1 flex-col justify-between pt-4">
          <div className="text-muted-foreground flex items-center justify-between px-4 font-mono text-[10px]">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <Zap className="size-3" />
              <Shield className="size-3" />
            </div>
          </div>

          <div className="flex flex-col items-center">
            <p className="text-foreground text-sm font-semibold">DocuID</p>
            <p className="text-muted-foreground mt-1 text-[11px]">Verify your identity</p>
          </div>

          <div className="relative flex h-35 items-center justify-center">
            {phase === 'idle' && (
              <div className="flex flex-col items-center gap-2">
                <Fingerprint className="text-muted-foreground/40 size-12" />
                <span className="text-muted-foreground text-[10px]">Tap to scan</span>
              </div>
            )}

            {phase === 'scanning' && (
              <div className="relative flex size-20 items-center justify-center">
                <Fingerprint className="text-primary size-12" />
                <div
                  className="absolute inset-0 overflow-hidden rounded-full"
                  style={{ clipPath: `inset(${String(100 - scanLine)}% 0 0 0)` }}
                >
                  <div
                    className="bg-primary absolute inset-x-0 h-0.5"
                    style={{
                      top: `${String(scanLine)}%`,
                      boxShadow: '0 0 8px var(--primary)',
                    }}
                  />
                </div>
                <div className="bg-primary/20 absolute -inset-2 animate-ping rounded-full" />
              </div>
            )}

            {phase === 'verifying' && (
              <div className="relative flex size-14 items-center justify-center">
                <div className="border-primary absolute inset-0 animate-spin rounded-full border-2 border-t border-r border-b-transparent border-l-transparent" />
                <ScanFace className="text-foreground size-7" />
              </div>
            )}

            {phase === 'success' && (
              <div className="bg-primary/15 text-primary flex size-16 items-center justify-center rounded-full">
                <CheckCircle2 className="size-9" />
              </div>
            )}
          </div>

          <div className="pb-6 text-center">
            {phase === 'idle' && <p className="text-muted-foreground text-[11px]">Waiting...</p>}
            {phase === 'scanning' && (
              <p className="text-primary text-[11px] font-medium">Scanning biometrics...</p>
            )}
            {phase === 'verifying' && (
              <p className="text-foreground text-[11px] font-medium">iVALT verifying...</p>
            )}
            {phase === 'success' && (
              <div className="space-y-1">
                <p className="text-foreground text-[11px] font-semibold">Verified</p>
                <p className="text-muted-foreground text-[10px]">Vault unlocked on device</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-muted mx-auto mb-2 h-1 w-20 rounded-full" />
      </div>

      <div className="border-border bg-card flex items-center gap-1.5 rounded-full border p-1 shadow-sm">
        {STAGES.map((stage, index) => {
          const currentIndex = PHASE_STAGE[phase];
          const status: 'done' | 'active' | 'pending' =
            index < currentIndex ? 'done' : index === currentIndex ? 'active' : 'pending';
          const Icon = stage.Icon;
          return (
            <Fragment key={stage.id}>
              <div
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-300',
                  status === 'active' &&
                    'bg-primary/15 text-primary ring-primary/30 shadow-[0_0_14px_color:var(--primary)] ring-1',
                  status === 'done' && 'bg-primary/10 text-primary',
                  status === 'pending' && 'text-muted-foreground',
                )}
              >
                <Icon
                  className={cn(
                    'size-3.5 transition-transform duration-300',
                    status === 'active' && 'scale-110',
                  )}
                />
                <span>{stage.label}</span>
              </div>
              {index < STAGES.length - 1 ? (
                <div className="flex items-center px-1" aria-hidden>
                  <div
                    className={cn(
                      'h-px w-5 rounded-full transition-colors duration-300',
                      index < currentIndex ? 'bg-primary' : 'bg-border',
                    )}
                  />
                </div>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
